/**
 * VideoPage Component
 *
 * Main video page with a standardized top sub-tab bar, organized by category:
 * - Production: AI Director (full video pipeline)
 * - Generate: Text to Video (T2V), Image to Video (I2V)
 * - Animate: Motion Control, Talking Heads
 *
 * The Lina AI assistant is no longer mounted here — she is a global floating
 * widget (see <LinaAssistant> in App). When she parses a config she dispatches
 * `apply-video-config`, which this page listens for to fill the video form.
 */

import { memo, useMemo, useState, useEffect } from 'react';
import { Video, Clapperboard, ImagePlus, Move, MessageSquare } from 'lucide-react';
import { VideoHero } from './VideoHero';
import { VideoTabContent } from './VideoTabContent';
import { VideoGenerationProvider } from './VideoGenerationContext';
import { SubTabBar, type SubTabItem } from '@/components/common';
import { useTranslation } from '@/hooks/useTranslation';
import type { VideoTab } from './types';
import type { VideoConfig } from '@/types';

interface VideoPageProps {
  isBackendConnected: boolean | null;
  /** Sub-tab to open initially (e.g. when Lina routes a config here). */
  initialSubTab?: string;
  /**
   * Config pushed in from outside the page (e.g. parsed by the global Lina
   * assistant). Held in App state so it survives this page mounting, then
   * forwarded to the video form. A new object reference re-applies it.
   */
  externalConfigUpdate?: Partial<VideoConfig> | null;
}

const VIDEO_TAB_IDS: VideoTab[] = [
  'full-video',
  'scene',
  'image-to-video',
  'motion-control',
  'talking-heads',
];

export const VideoPage = memo(function VideoPage({
  isBackendConnected,
  initialSubTab,
  externalConfigUpdate = null,
}: VideoPageProps) {
  const { t } = useTranslation();
  const isValidTab = (id?: string): id is VideoTab =>
    id != null && (VIDEO_TAB_IDS as string[]).includes(id);

  const [activeTab, setActiveTab] = useState<VideoTab>(
    isValidTab(initialSubTab) ? initialSubTab : 'full-video'
  );

  // React to deep-links / Lina navigation into a specific sub-tab.
  useEffect(() => {
    if (isValidTab(initialSubTab)) setActiveTab(initialSubTab);
  }, [initialSubTab]);

  const videoTabs: SubTabItem[] = useMemo(
    () => [
      {
        id: 'full-video',
        label: t('video.tabs.fullVideo', { defaultValue: 'AI Director' }),
        icon: Video,
        group: t('video.categories.production', { defaultValue: 'Production' }),
      },
      {
        id: 'scene',
        label: t('video.tabs.scene', { defaultValue: 'Text to Video' }),
        icon: Clapperboard,
        group: t('video.categories.generate', { defaultValue: 'Generate' }),
      },
      {
        id: 'image-to-video',
        label: t('video.tabs.imageToVideo', { defaultValue: 'Image to Video' }),
        icon: ImagePlus,
        group: t('video.categories.generate', { defaultValue: 'Generate' }),
      },
      {
        id: 'motion-control',
        label: t('video.tabs.motionControl', { defaultValue: 'Motion Control' }),
        icon: Move,
        group: t('video.categories.animate', { defaultValue: 'Animate' }),
      },
      {
        id: 'talking-heads',
        label: t('video.tabs.talkingHeads', { defaultValue: 'Talking Heads' }),
        icon: MessageSquare,
        group: t('video.categories.animate', { defaultValue: 'Animate' }),
      },
    ],
    [t]
  );

  return (
    <div className="flex flex-col flex-1 min-w-0">
      <div className="px-4 sm:px-6 lg:px-8 pt-4 sm:pt-8">
        <VideoHero />
      </div>

      <SubTabBar
        tabs={videoTabs}
        activeId={activeTab}
        onChange={(id) => setActiveTab(id as VideoTab)}
        accent="purple"
        ariaLabel={t('video.hero.title', { defaultValue: 'AI Video Studio' })}
      />

      <main className="flex-1 min-w-0 px-4 sm:px-6 lg:px-8 py-4 sm:py-8" data-testid="main-content">
        <VideoGenerationProvider
          isBackendConnected={isBackendConnected}
          externalConfigUpdate={externalConfigUpdate}
        >
          <VideoTabContent activeTab={activeTab} />
        </VideoGenerationProvider>
      </main>
    </div>
  );
});
